import React from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend } from "recharts";
import { formatCurrency } from "../utils/currencyFormatter";

const CategoryChart = ({ category, data, isYearlyView, incomeAvg, months, userCurrency }) => {
  if (!data || data.length === 0) return <p>No data available</p>;

  const monthOrder = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
  };

  let sortedData = [...data];
  
  if (isYearlyView) {
    sortedData.sort((a, b) => a.year - b.year);
  } else {
    sortedData.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return monthOrder[a.month] - monthOrder[b.month];
    });
  }

  const values = sortedData.map((item) => Math.round(item.amount));
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;

  const percentage = category !== "income" ? ((avg / incomeAvg) * 100).toFixed(1) : null;
  const avgLabel = category === "income"
    ? `Average ${category.charAt(0).toUpperCase() + category.slice(1)} (${formatCurrency(avg.toFixed(0), userCurrency)})`
    : `Average ${category.charAt(0).toUpperCase() + category.slice(1)} (${formatCurrency(avg.toFixed(0), userCurrency)}, ${percentage}%)`;

  const formatLabelForDisplay = (item, monthCount) => {
    if (isYearlyView) {
      return `Year ${item.year}`;
    }
    
    if (monthCount >= 12) {
      const shortYear = item.year.toString().substring(2);
      const shortMonth = item.month.substring(0, 3);
      return `${shortMonth} '${shortYear}`;
    }
    
    return `${item.month} ${item.year}`;
  };

  const chartData = sortedData.map((item) => ({
    name: formatLabelForDisplay(item, months),
    amount: Math.round(item.amount),
    average: values.length > 1 ? parseFloat(avg) : null
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.98)', 
          padding: '12px 16px', 
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          fontWeight: '500',
          backdropFilter: 'blur(8px)'
        }}>
          <p style={{ margin: '0 0 4px 0', color: '#333', fontWeight: '600' }}>
            {label}
          </p>
          {payload.map((pld, index) => (
            <p key={index} style={{ color: pld.color, margin: '2px 0' }}>
              {pld.dataKey === 'average' 
                ? `Avg: ${formatCurrency(pld.value.toFixed(0), userCurrency)} ${percentage ? '('+percentage+'%)' : ''}` 
                : `${category.charAt(0).toUpperCase() + category.slice(1)}: ${formatCurrency(pld.value, userCurrency)}`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomizedLabel = ({ x, y, width, height, value }) => {
    if (!value) return null;
    return (
      <text 
        x={x + width / 2} 
        y={y + height / 2} 
        fill="black" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize={months >= 12 ? "12" : "14"}
      >
        {value.toFixed(0)}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 20, right: 0, left: 0, bottom: months >= 12 ? 40 : 10 }}>
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: months >= 12 ? 11 : 14, fill: 'black' }}
          interval={0}
          angle={months >= 12 ? -35 : 0}
          textAnchor={months >= 12 ? "end" : "middle"}
          height={months >= 12 ? 70 : 40}
        />
        <YAxis tick={{ fontSize: 14, fill: 'black' }} />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontSize: '15px' }}
          iconType="line"
          formatter={(value) => value || ''}
        />
        <Bar dataKey="amount" fill="#2196f3">
          <LabelList content={<CustomizedLabel />} />
        </Bar>
        {values.length > 1 && (
          <Line 
            type="monotone" 
            dataKey="average" 
            stroke="red" 
            strokeWidth={2}
            dot={{ fill: 'red', r: 3 }}
            name={avgLabel}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default CategoryChart;